<template>
  <div class="layout-options">
    <div class="layout-options__info">
      <span class="mx-2">
        <template v-if="hasActiveLayout">
          {{ $t('grid.layouts.sidebar.currentLayout') }}:
          <span class="layout-name">{{ activeLayoutName }}</span>
        </template>
        <span v-else class="layout-name--no-layout">
          {{ $t('grid.layouts.sidebar.noActiveLayout') }}
        </span>
      </span>
    </div>
    <div class="layout-options__buttons">
      <!-- Save button (update existing) - only shown when user can edit -->
      <v-btn
        v-if="canEdit && hasActiveLayout"
        class="mx-1 px-2"
        color="primary"
        depressed
        small
        height="24"
        @click="updateLayout"
      >
        {{ $t('grid.layouts.sidebar.saveLayout') }}
      </v-btn>
      <!-- Save as new button -->
      <v-btn
        class="mx-1 px-2"
        color="primary"
        :outlined="(canEdit || isRecommendedLayout) && hasActiveLayout"
        :depressed="!((canEdit || isRecommendedLayout) && hasActiveLayout)"
        small
        height="24"
        @click="openSaveDialog"
      >
        {{
          (canEdit || isRecommendedLayout) && hasActiveLayout
            ? $t('grid.layouts.sidebar.saveAsNew')
            : $t('grid.layouts.sidebar.saveLayout')
        }}
      </v-btn>
    </div>
    <confirm-dialog
      class="confirm-dialog"
      :is-open="saveOpen"
      :ok-btn-text="$t('actions.save')"
      :cancel-btn-text="$t('actions.cancel')"
      :disable-ok-button="!isLayoutNameValid"
      @onOk="saveLayout"
      @onCancel="closeModal"
    >
      <template #body>
        {{ $t('grid.layouts.saveDialog.title') }}
        <div class="layout-save-dialog">
          <v-text-field
            ref="layoutNameField"
            v-model="newLayoutName"
            :label="$t('grid.layouts.saveDialog.layoutName')"
            outlined
            dense
            :rules="rules"
          />
        </div>
      </template>
    </confirm-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'LayoutOptions',
  props: {
    activeLayoutName: {
      type: String,
      default: '',
    },
    hasActiveLayout: {
      type: Boolean,
      default: false,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
    isRecommendedLayout: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      saveOpen: false,
      newLayoutName: '',
      rules: [this.valueRequired, this.minLength, this.uniqueName],
    };
  },
  computed: {
    ...mapGetters('gridLayouts', ['getLayouts']),
    isLayoutNameValid() {
      return !this.rules.some(rule => rule(this.newLayoutName) !== true);
    },
  },
  methods: {
    valueRequired(v) {
      return !!v.trim() || this.$t('grid.layouts.validation.nameRequired');
    },
    minLength(v) {
      return v.trim().length >= 3 || this.$t('grid.layouts.validation.nameTooShort');
    },
    uniqueName(v) {
      return (
        !this.getLayouts
          .map(layout => layout.name.toLowerCase())
          .includes(v.trim().toLowerCase()) || this.$t('grid.layouts.validation.nameAlreadyExists')
      );
    },
    openSaveDialog() {
      this.newLayoutName = '';
      this.saveOpen = true;
      // focus on the layout name field
      this.$nextTick(() => {
        setTimeout(() => {
          this.$refs.layoutNameField.focus();
        }, 100);
      });
    },
    closeModal() {
      this.saveOpen = false;
      this.newLayoutName = '';
    },
    saveLayout() {
      if (!this.isLayoutNameValid) return;
      this.$emit('save', {
        name: this.newLayoutName.trim(),
        closeModal: this.closeModal,
      });
    },
    updateLayout() {
      this.$emit('update');
    },
  },
};
</script>

<style lang="scss" scoped>
.layout-options {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 4px;

  &__info {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
  }

  &__buttons {
    display: flex;
    align-items: center;
  }
}

.layout-save-dialog {
  margin-top: 8px;
}

.layout-name {
  font-weight: bold;
  text-transform: capitalize;
}

.layout-name--no-layout {
  font-style: italic;
  color: var(--v-warning-base, #fb8c00);
}

.confirm-dialog {
  position: absolute;
}
</style>
